import type { MaybeRefOrGetter } from 'vue'

import { useFilterDraft } from './useFilterDraft'

/** What a `filter-*` widget emits: the edited values, or a request to clear them. */
export type FilterFormEmit<T> = {
  (e: 'apply', value: T): void
  (e: 'reset'): void
}

/**
 * A controlled filter form: the draft it edits and the two handlers its buttons call.
 *
 * Every `filter-*` widget's script is this call: bind `draft` to the fields, `onApply` to
 * the Apply button and `onReset` to Reset, and the widget itself holds nothing else.
 *
 * The URL owns the filters, so the widget never writes them — `onApply` hands a snapshot
 * upwards and `onReset` clears the draft before saying so. `defineProps`/`defineEmits`
 * cannot move out of an SFC, so the widget declares them and passes its own `emit` in here.
 */
export function useFilterForm<T extends object>(
  initial: T,
  current: MaybeRefOrGetter<Partial<T> | undefined>,
  emit: FilterFormEmit<T>
) {
  const { draft, snapshot, reset } = useFilterDraft<T>(initial, current)

  function onApply() {
    emit('apply', snapshot())
  }

  function onReset() {
    reset()
    emit('reset')
  }

  return { draft, snapshot, onApply, onReset }
}
